import React from 'react';
import { useTranslation } from 'react-i18next';
import { Sparkles, Coins, ArrowRight, CreditCard, BarChart3, CheckCircle2 } from 'lucide-react';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from 'components/ui/dialog';
import { Button } from 'components/ui/button';
import { formatGroupedNumber } from 'lib/dateFormat';

export interface WelcomeBonusDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  balanceSom?: number;
  userName?: string;
  onStartTests?: () => void;
}

export const WelcomeBonusDialog: React.FC<WelcomeBonusDialogProps> = ({
  open,
  onOpenChange,
  balanceSom,
  userName,
  onStartTests,
}) => {
  const { t } = useTranslation();

  const displayBalance = typeof balanceSom === 'number' && balanceSom > 0 ? balanceSom : 0;

  const handleStartTests = () => {
    if (onStartTests) {
      onStartTests();
    } else {
      onOpenChange(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-[480px] p-0 overflow-hidden border-border/80 bg-card shadow-2xl">
        {/* Ambient Top Glow & Header */}
        <div className="relative bg-gradient-to-b from-emerald-500/15 via-emerald-500/5 to-transparent px-6 pt-7 pb-4 text-center sm:px-7">
          <div
            aria-hidden="true"
            className="pointer-events-none absolute -top-12 left-1/2 -translate-x-1/2 h-36 w-48 rounded-full bg-emerald-500/20 blur-3xl"
          />

          <div className="relative">
            {/* Pill Badge */}
            <div className="inline-flex items-center gap-1.5 rounded-full border border-emerald-500/30 bg-emerald-500/10 px-3 py-1 text-xs font-bold text-emerald-700 dark:text-emerald-300">
              <Sparkles className="h-3.5 w-3.5 text-emerald-600 dark:text-emerald-400" />
              <span>{t('portal.welcome_dialog_badge', 'Xush kelibsiz bonusi')}</span>
            </div>

            {/* Glowing Icon Container */}
            <div className="mx-auto mt-4 mb-3 flex h-14 w-14 items-center justify-center rounded-2xl bg-gradient-to-br from-emerald-500 to-teal-600 text-white shadow-lg shadow-emerald-500/25 ring-4 ring-emerald-500/15">
              <Coins className="h-7 w-7" />
            </div>

            <DialogHeader className="space-y-1.5 text-center sm:text-center">
              <DialogTitle className="text-xl font-black tracking-tight text-foreground sm:text-2xl">
                {userName
                  ? t('portal.welcome_dialog_title_named', { name: userName })
                  : t('portal.welcome_dialog_title', 'Tabriklaymiz, profilingiz tayyor!')}
              </DialogTitle>
              <DialogDescription className="text-[13px] leading-relaxed text-muted-foreground sm:text-sm">
                {t(
                  'portal.welcome_dialog_desc',
                  "Examing platformasiga xush kelibsiz. O'quv jarayonini darhol boshlashingiz uchun hisobingizga boshlang'ich sinov mablag'i biriktirildi.",
                )}
              </DialogDescription>
            </DialogHeader>
          </div>
        </div>

        {/* Modal Body */}
        <div className="space-y-4 px-6 sm:px-7">
          {/* Highlighted Balance Card */}
          <div className="relative overflow-hidden rounded-xl border border-emerald-200/90 bg-gradient-to-br from-emerald-50/80 via-background to-emerald-50/40 p-4 shadow-sm dark:border-emerald-900/60 dark:from-emerald-950/40 dark:via-background dark:to-emerald-950/20">
            <div className="flex items-center justify-between text-xs">
              <span className="flex items-center gap-1.5 font-semibold text-muted-foreground">
                <span className="h-2 w-2 rounded-full bg-emerald-500 animate-pulse" />
                {t('portal.welcome_dialog_balance_label', "Hisobingizdagi mablag'")}
              </span>
              <span className="rounded-md bg-emerald-500/10 px-2 py-0.5 text-[11px] font-bold uppercase tracking-wider text-emerald-700 dark:text-emerald-300">
                {t('portal.welcome_dialog_balance_badge', 'Faol')}
              </span>
            </div>

            <div className="mt-2.5 flex items-baseline gap-2">
              <span className="text-3xl font-black tabular-nums tracking-tight text-emerald-950 dark:text-emerald-100 sm:text-4xl">
                {formatGroupedNumber(displayBalance)}
              </span>
              <span className="text-sm font-bold text-muted-foreground sm:text-base">
                {t('portal_tests_currency', "so'm")}
              </span>
            </div>

            <p className="mt-1 text-[11px] font-medium text-emerald-700/80 dark:text-emerald-400/80">
              {t('portal.welcome_dialog_balance_sub', "Boshlang'ich bonus · Muddatsiz")}
            </p>
          </div>

          {/* Value Prop Explanations */}
          <div className="space-y-2.5">
            <h4 className="text-xs font-bold uppercase tracking-wider text-muted-foreground">
              {t('portal.welcome_dialog_how_to_use_title', "Ushbu mablag'dan qanday foydalanasiz?")}
            </h4>

            <ul className="space-y-2 text-xs">
              <li className="flex items-start gap-3 rounded-lg border border-border/70 bg-card p-2.5 transition-colors hover:bg-muted/40">
                <div className="flex h-7 w-7 shrink-0 items-center justify-center rounded-md bg-emerald-500/10 text-emerald-600 dark:text-emerald-400">
                  <CheckCircle2 className="h-4 w-4" />
                </div>
                <div className="min-w-0">
                  <div className="font-semibold text-foreground">
                    {t('portal.welcome_dialog_feature_tests_title', 'Pullik mock testlarni boshlash')}
                  </div>
                  <div className="text-[11.5px] leading-snug text-muted-foreground">
                    {t(
                      'portal.welcome_dialog_feature_tests_desc',
                      'Katalogdagi istalgan mock testni tanlang va bir zumda topshirishni boshlang.',
                    )}
                  </div>
                </div>
              </li>

              <li className="flex items-start gap-3 rounded-lg border border-border/70 bg-card p-2.5 transition-colors hover:bg-muted/40">
                <div className="flex h-7 w-7 shrink-0 items-center justify-center rounded-md bg-sky-500/10 text-sky-600 dark:text-sky-400">
                  <CreditCard className="h-4 w-4" />
                </div>
                <div className="min-w-0">
                  <div className="font-semibold text-foreground">
                    {t('portal.welcome_dialog_feature_card_title', 'Karta talab etilmaydi')}
                  </div>
                  <div className="text-[11.5px] leading-snug text-muted-foreground">
                    {t(
                      'portal.welcome_dialog_feature_card_desc',
                      'Mablag\' to\'g\'ridan-to\'g\'ri balansingizdan yechiladi — kartasiz va ortiqcha to\'lovlarsiz.',
                    )}
                  </div>
                </div>
              </li>

              <li className="flex items-start gap-3 rounded-lg border border-border/70 bg-card p-2.5 transition-colors hover:bg-muted/40">
                <div className="flex h-7 w-7 shrink-0 items-center justify-center rounded-md bg-violet-500/10 text-violet-600 dark:text-violet-400">
                  <BarChart3 className="h-4 w-4" />
                </div>
                <div className="min-w-0">
                  <div className="font-semibold text-foreground">
                    {t('portal.welcome_dialog_feature_analysis_title', 'Chuqur tahlil va natijalar')}
                  </div>
                  <div className="text-[11.5px] leading-snug text-muted-foreground">
                    {t(
                      'portal.welcome_dialog_feature_analysis_desc',
                      'Har bir test yakunida javoblar tahlili va zaif mavzular bo\'yicha hisobot oling.',
                    )}
                  </div>
                </div>
              </li>
            </ul>
          </div>
        </div>

        {/* Modal Footer Actions */}
        <div className="mt-2 flex flex-col gap-2.5 px-6 pb-6 pt-2 sm:flex-row sm:px-7 sm:pb-7">
          <Button
            type="button"
            onClick={handleStartTests}
            className="h-11 w-full gap-2 bg-emerald-600 text-sm font-bold text-white hover:bg-emerald-700 dark:bg-emerald-600 dark:hover:bg-emerald-500 sm:flex-1"
          >
            <span>{t('portal.welcome_dialog_cta_start', 'Mock testlarni ko\'rish')}</span>
            <ArrowRight className="h-4 w-4" />
          </Button>

          <Button
            type="button"
            variant="outline"
            onClick={() => onOpenChange(false)}
            className="h-11 w-full text-sm font-semibold sm:w-auto"
          >
            {t('portal.welcome_dialog_cta_dismiss', 'Tushunarli')}
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
};

export default WelcomeBonusDialog;
